const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class ApiService {
  // Helper method for making requests
  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;
    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    };

    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'An error occurred');
    }

    return data;
  }

  // Helper for authenticated requests (user)
  async userRequest(endpoint, options = {}) {
    const token = localStorage.getItem('userToken');
    if (!token) {
      throw new Error('Not authenticated');
    }

    return this.request(endpoint, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`
      }
    });
  }

  // Helper for authenticated requests (admin)
  async adminRequest(endpoint, options = {}) {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      window.location.href = '/admin/login';
      throw new Error('Not authenticated');
    }

    try {
      return await this.request(endpoint, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${token}`
        }
      });
    } catch (error) {
      if (error.message.includes('401') || error.message.includes('unauthorized')) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminData');
        window.location.href = '/admin/login';
      }
      throw error;
    }
  }

  // ===== APARTMENTS =====
  async getApartments(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/apartments${query ? `?${query}` : ''}`);
  }

  async getFeaturedApartments() {
    return this.request('/apartments/featured');
  }

  async getApartmentBySlug(slug) {
    return this.request(`/apartments/slug/${slug}`);
  }

  async getApartmentById(id) {
    return this.request(`/apartments/${id}`);
  }

  async checkAvailability(id, checkIn, checkOut) {
    return this.request(`/apartments/${id}/availability?checkIn=${checkIn}&checkOut=${checkOut}`);
  }

  // ===== LOCATIONS =====
  async getLocations() {
    return this.request('/locations');
  }

  async getFeaturedLocations() {
    return this.request('/locations/featured');
  }

  async getLocationBySlug(slug) {
    return this.request(`/locations/${slug}`);
  }

  // ===== BOOKINGS =====
  async calculatePrice(data) {
    return this.request('/bookings/calculate-price', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async createBooking(data) {
    const token = localStorage.getItem('userToken');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    return this.request('/bookings', {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
  }

  async getBookingById(id) {
    return this.request(`/bookings/${id}`);
  }

  async getBookingByReference(reference, email) {
    return this.request(`/bookings/reference/${reference}?email=${email}`);
  }

  async confirmBooking(id, paymentData) {
    return this.request(`/bookings/${id}/confirm`, {
      method: 'PUT',
      body: JSON.stringify(paymentData)
    });
  }

  async cancelBooking(id, reason) {
    return this.request(`/bookings/${id}/cancel`, {
      method: 'PUT',
      body: JSON.stringify({ reason })
    });
  }

  // ===== USER AUTH =====
  async registerUser(data) {
    return this.request('/users/register', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async loginUser(email, password) {
    return this.request('/users/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
  }

  async getUserProfile() {
    return this.userRequest('/users/profile');
  }

  async updateUserProfile(data) {
    return this.userRequest('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async changePassword(currentPassword, newPassword) {
    return this.userRequest('/users/change-password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword })
    });
  }

  async getUserBookings(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.userRequest(`/bookings/user/my-bookings${query ? `?${query}` : ''}`);
  }

  async getSavedApartments() {
    return this.userRequest('/users/saved');
  }

  async saveApartment(apartmentId) {
    return this.userRequest(`/users/saved/${apartmentId}`, {
      method: 'POST'
    });
  }

  async removeSavedApartment(apartmentId) {
    return this.userRequest(`/users/saved/${apartmentId}`, {
      method: 'DELETE'
    });
  }

  // ===== REVIEWS =====
  async getApartmentReviews(apartmentId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/reviews/apartment/${apartmentId}${query ? `?${query}` : ''}`);
  }

  async createReview(data) {
    return this.userRequest('/reviews', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getUserReviews() {
    return this.userRequest('/reviews/my-reviews');
  }

  // ===== CONTACT =====
  async submitContactForm(data) {
    return this.request('/contact', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // ===== PAYMENTS =====
  async createPaymentIntent(bookingId) {
    return this.request('/payments/create-intent', {
      method: 'POST',
      body: JSON.stringify({ bookingId })
    });
  }

  async createCheckoutSession(bookingId) {
    return this.request('/payments/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ bookingId })
    });
  }

  async verifyPaymentSession(sessionId) {
    return this.request(`/payments/verify-session/${sessionId}`);
  }

  // ===== ADMIN AUTH =====
  async adminLogin(email, password) {
    return this.request('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
  }

  // ===== ADMIN DASHBOARD =====
  async getAdminStats() {
    return this.adminRequest('/admin/dashboard');
  }

  // ===== ADMIN APARTMENTS =====
  async createApartment(data) {
    return this.adminRequest('/apartments', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateApartment(id, data) {
    return this.adminRequest(`/apartments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteApartment(id) {
    return this.adminRequest(`/apartments/${id}`, {
      method: 'DELETE'
    });
  }

  async uploadApartmentImages(formData) {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_URL}/apartments/upload-images`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: formData
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message);
    return data;
  }

  // ===== ADMIN LOCATIONS =====
  async createLocation(data) {
    return this.adminRequest('/locations', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateLocation(id, data) {
    return this.adminRequest(`/locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteLocation(id) {
    return this.adminRequest(`/locations/${id}`, {
      method: 'DELETE'
    });
  }

  async uploadLocationImage(formData) {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_URL}/locations/upload-image`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: formData
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message);
    return data;
  }

  // ===== ADMIN BOOKINGS =====
  async getAdminBookings(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.adminRequest(`/admin/bookings${query ? `?${query}` : ''}`);
  }

  async updateBookingStatus(id, status) {
    return this.adminRequest(`/admin/bookings/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  }

  // ===== ADMIN REVIEWS =====
  async getAdminReviews(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.adminRequest(`/admin/reviews${query ? `?${query}` : ''}`);
  }

  async moderateReview(id, status) {
    return this.adminRequest(`/admin/reviews/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  }

  async deleteReview(id) {
    return this.adminRequest(`/admin/reviews/${id}`, {
      method: 'DELETE'
    });
  }

  // ===== ADMIN USERS =====
  async getAdminUsers(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.adminRequest(`/admin/users${query ? `?${query}` : ''}`);
  }

  async updateUserStatus(id, isActive) {
    return this.adminRequest(`/admin/users/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ isActive })
    });
  }

  // ===== ADMIN SETTINGS =====
  async getSettings() {
    return this.adminRequest('/admin/settings');
  }

  async updateSettings(data) {
    return this.adminRequest('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }
}

const api = new ApiService();
export default api;
